import { IRynReplayEnvelope } from './ryn-replay-batcher';

/**
 * Batch transport for the replay stream (#30070 capture slice).
 *
 * WIRE CONTRACT, mirrored from `IngestFunction`'s own header comment:
 *   POST {ingestUrl}
 *   X-Ryn-Replay-Key: <per-app shared key>
 *     OR Authorization: Bearer <signed ingest token> (#30115, opt-in per app)
 *   body: gzip of a JSON ARRAY of envelopes
 *   202 Accepted on success
 *
 * Gzip is NOT optional. `GzipBatchReader` returns Malformed (400) for a body
 * that is not valid gzip, so an uncompressed send fails the whole batch.
 */

/** Header the ingest key travels in. Matches `ReplayIngestKeyResolver.KeyHeaderName`. */
export const RYN_REPLAY_KEY_HEADER = 'X-Ryn-Replay-Key';

/**
 * Ceiling on a COMPRESSED body we are willing to send with `keepalive`.
 *
 * The spec quota is 64 KiB shared across every in-flight keepalive request in
 * the document, so the safe number is not 64 KiB: something else may already be
 * holding part of it. 48 KiB leaves headroom for a concurrent batch plus header
 * overhead.
 *
 * This constant exists because the previous fix asserted the premise instead of
 * enforcing it. The comment said "batches are small on the unload path" and
 * nothing checked, while `RynReplayBatcher.add` documents the opposite as a
 * designed case: a single envelope at or over the byte cap is emitted as its own
 * batch, "rrweb full snapshots of a dense Kendo grid genuinely can exceed a
 * conservative byte cap on their own". Two files, contradictory premises, no
 * guard between them.
 */
export const RYN_REPLAY_KEEPALIVE_MAX_BYTES = 48 * 1024;

export interface IRynReplaySendOptions {
  /**
   * Only set this on the final flush as the page goes away.
   *
   * `keepalive` lets a request outlive the document, which is the only way the
   * last batch of a session survives a tab close. It costs something real
   * though: the browser caps the total body size of keepalive requests, and
   * measured in Chrome the whole request fails once the batch gets anywhere
   * near that cap. So it is right for the unload flush, where batches are small
   * and the alternative is losing them, and wrong everywhere else.
   *
   * Treated as a REQUEST, not an instruction: `send` drops it when the
   * compressed body exceeds `RYN_REPLAY_KEEPALIVE_MAX_BYTES`, because an
   * oversize keepalive request is a guaranteed rejection whereas the same body
   * without keepalive at least completes when the page survives (a same-tab
   * navigation, a `visibilitychange` that never becomes a close). Guaranteed
   * loss versus partial loss is not a close call.
   */
  keepalive?: boolean;
}

export interface IRynReplayTransport {
  /** Resolves true when the batch was accepted and may be discarded. */
  send(
    envelopes: IRynReplayEnvelope[],
    options?: IRynReplaySendOptions
  ): Promise<boolean>;
}

export interface IRynReplayTransportConfig {
  ingestUrl: string;
  ingestKey: string;
  /**
   * Extra request headers. Exists for ONE case and should not grow past it:
   * ingest denies a request with no `Origin` (see `ReplayOriginAllowlist` —
   * "a missing or blank Origin header is DENIED"), and a native HTTP stack
   * sends none, so the NativeScript recorder supplies the value its app is
   * registered under in `RynReplay:AllowedOrigins`.
   *
   * A browser MUST NOT use this for `Origin`. It is a forbidden header name
   * there, so the assignment is dropped and the browser's own (correct) value
   * is sent regardless — meaning a web caller that sets it here has written a
   * line that reads as load-bearing and does nothing.
   */
  extraHeaders?: Readonly<Record<string, string>>;
  /**
   * Present only when the app provided `RYN_REPLAY_BEARER_PROVIDER`. Absent,
   * the transport sends the key exactly as before and never calls the mint.
   */
  ingestTokens?: IRynReplayIngestTokenSource;
}

/** What the transport needs from a token source. */
export interface IRynReplayIngestTokenSource {
  /** A token for this session, minting at most once. `null` means use the key. */
  getToken(sessionId: string): Promise<string | null>;
  /** The cached token for this session if still valid. Never mints. */
  peekToken(sessionId: string): string | null;
  /** Drop this session's cached token, e.g. after ingest refused it. */
  invalidate(sessionId: string): void;
  /**
   * Ingest refused even a freshly minted token: stop minting for a while so
   * later batches go straight to the key instead of repeating the recovery.
   */
  reportRejected(): void;
}

/**
 * Compress a UTF-8 string to gzip bytes. Injectable so the suite can assert the
 * transport's framing without a browser compression implementation.
 */
export type RynReplayCompressor = (json: string) => Promise<Uint8Array>;

/**
 * Schedule work for a quiet moment. Used so gzip+POST of ordinary flushes does
 * not contend with input/paint on the main thread. Unload flushes bypass this.
 *
 * Prefer `requestIdleCallback` with a timeout so a busy tab still drains; fall
 * back to `setTimeout` when the API is missing (older Safari / jsdom).
 */
export type RynReplayIdleScheduler = (
  work: () => void,
  options?: { timeoutMs?: number }
) => void;

/** Default idle budget before we force the flush anyway. */
export const RYN_REPLAY_IDLE_TIMEOUT_MS = 2_000;

export const scheduleRynReplayIdle: RynReplayIdleScheduler = (
  work,
  options
) => {
  const timeoutMs = options?.timeoutMs ?? RYN_REPLAY_IDLE_TIMEOUT_MS;
  const ric = (
    globalThis as {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number }
      ) => number;
    }
  ).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(() => work(), { timeout: timeoutMs });
    return;
  }
  setTimeout(work, 0);
};

/** Test double: run work on the next microtask (still async, no fake timers). */
export const scheduleRynReplayIdleImmediate: RynReplayIdleScheduler = (
  work
) => {
  queueMicrotask(work);
};

/**
 * Default compressor: the platform `CompressionStream`, available in Chrome and
 * Edge (the internal browser set) and Safari 16.4+. Deliberately has NO
 * uncompressed fallback: the server rejects non-gzip with a 400, so a fallback
 * would convert an unsupported-browser case into a silent per-batch failure
 * loop. Absent support throws once, at start, where it is visible.
 */
export const gzipRynReplayBatch: RynReplayCompressor = async (json: string) => {
  const CompressionStreamRef = (
    globalThis as { CompressionStream?: new (format: string) => unknown }
  ).CompressionStream;

  if (typeof CompressionStreamRef !== 'function') {
    throw new Error(
      'Ryn Replay: CompressionStream is unavailable, so replay batches cannot be gzipped and the ingest endpoint would reject them.'
    );
  }

  const stream = new Response(json).body?.pipeThrough(
    new CompressionStreamRef('gzip') as ReadableWritablePair<
      Uint8Array,
      Uint8Array
    >
  );
  if (!stream) {
    throw new Error('Ryn Replay: could not open a gzip stream for the batch.');
  }

  const compressed = await new Response(stream).arrayBuffer();
  return new Uint8Array(compressed);
};

/**
 * HTTP transport.
 *
 * Uses `keepalive` only when the caller asks for it, which in practice means
 * the page-hide flush. This used to be on for every request and that was a real
 * bug, caught the first time the recorder was pointed at a live app:
 *
 * The browser enforces a small quota on keepalive request bodies, and a fetch
 * that exceeds it rejects outright rather than degrading. Measured in Chrome
 * against a running app, a 10 KB body went through and a 60 KB body threw
 * "Failed to fetch"; the same 300 KB body sent fine without keepalive.
 *
 * The batch that always exceeds it is the rrweb full snapshot, because it is a
 * serialization of the entire DOM. So every session lost the one event that
 * every later event is a delta against, while all the small incremental batches
 * kept returning 202. Recordings stored, ingest looked healthy, and nothing was
 * replayable. Nothing errored anywhere a person would look.
 *
 * Non-unload flushes schedule compress+POST on an idle callback so gzip does
 * not steal frames from the host app. The unload/`keepalive` path stays
 * immediate — there is no idle left once the document is going away.
 */
export class RynReplayHttpTransport implements IRynReplayTransport {
  constructor(
    private readonly config: IRynReplayTransportConfig,
    private readonly compress: RynReplayCompressor = gzipRynReplayBatch,
    private readonly fetchRef: typeof fetch = globalThis.fetch?.bind(
      globalThis
    ),
    private readonly scheduleIdle: RynReplayIdleScheduler = scheduleRynReplayIdle
  ) {}

  async send(
    envelopes: IRynReplayEnvelope[],
    options?: IRynReplaySendOptions
  ): Promise<boolean> {
    if (envelopes.length === 0) {
      return true;
    }
    if (!this.config.ingestUrl || !this.config.ingestKey) {
      // Fail closed and say so once per batch rather than posting a session's
      // events at an unauthenticated endpoint.
      return false;
    }

    // Unload asked for keepalive: compress+POST now. Ordinary flushes wait for
    // idle so CompressionStream work stays off the interactive path.
    if (options?.keepalive) {
      return this.compressAndPost(envelopes, true);
    }

    return new Promise<boolean>((resolve, reject) => {
      this.scheduleIdle(() => {
        this.compressAndPost(envelopes, false).then(resolve, reject);
      });
    });
  }

  private async compressAndPost(
    envelopes: IRynReplayEnvelope[],
    keepaliveRequested: boolean
  ): Promise<boolean> {
    const body = await this.compress(JSON.stringify(envelopes));

    // Measured on the COMPRESSED body, which is what the browser actually
    // counts against the quota. Sizing the JSON instead would reject batches
    // that gzip down to a few KB, and a full snapshot compresses hard.
    const keepalive =
      keepaliveRequested && body.byteLength <= RYN_REPLAY_KEEPALIVE_MAX_BYTES;

    const response = await this.postAuthenticated(
      envelopes,
      body,
      keepalive,
      keepaliveRequested
    );

    if (!response.ok) {
      // The status alone is not enough to debug a 400: ingest names the
      // validator failure in the body (app mismatch, bad gzip, bad type).
      // `.text` is optional because some tests stub a status-only Response.
      const detail =
        typeof response.text === 'function'
          ? await response.text().catch(() => '')
          : '';
      console.warn(
        `Ryn Replay ingest ${response.status}`,
        detail.slice(0, 300)
      );
    }

    return response.ok;
  }

  /**
   * Post with the signed ingest token when one is available, else the key.
   *
   * On a 401 to a TOKEN batch: drop the token, mint ONE fresh one and retry
   * once; if that also 401s (or no fresh token), retry once with the key,
   * which the server still accepts while AcceptSharedIngestKey is on. At most
   * three posts and two mints per batch, then the caller's retry queue takes
   * over. A 401 on a KEY batch is not retried, exactly as before.
   *
   * On a 503 to a TOKEN batch: ingest has no signing key (a rollback), so a
   * re-mint would 503 too. Drop the token and retry once with the key; the
   * same applies when the 503 answers the re-minted retry. A 503 on a KEY
   * batch is not retried. A 401 on a freshly minted token puts the token
   * source into backoff, so later batches skip straight to the key.
   *
   * On unload the key retry after a 401 is sequential and often never leaves
   * before the page is gone; the batch then survives only through the
   * existing offline store.
   *
   * The unload (`keepalive`) path never mints: there is no time for a round
   * trip while the document goes away, so it uses a cached token or the key.
   * There is no sendBeacon path in the recorder (sendBeacon cannot set
   * headers); the unload flush is a keepalive fetch, which can carry either.
   */
  private async postAuthenticated(
    envelopes: IRynReplayEnvelope[],
    body: Uint8Array,
    keepalive: boolean,
    unloading: boolean
  ): Promise<Response> {
    const tokens = this.config.ingestTokens;
    const sessionId = singleSessionId(envelopes);
    if (!tokens || !sessionId) {
      return this.post(body, keepalive, null);
    }

    const token = unloading
      ? tokens.peekToken(sessionId)
      : await tokens.getToken(sessionId);
    let response = await this.post(body, keepalive, token);
    if (!token || (response.status !== 401 && response.status !== 503)) {
      return response;
    }

    tokens.invalidate(sessionId);
    if (response.status === 503) {
      return this.post(body, keepalive, null);
    }
    const fresh = unloading ? null : await tokens.getToken(sessionId);
    if (fresh) {
      response = await this.post(body, keepalive, fresh);
      if (response.status === 401) {
        tokens.invalidate(sessionId);
        tokens.reportRejected();
      } else if (response.status === 503) {
        tokens.invalidate(sessionId);
      }
    }
    if (response.status === 401 || response.status === 503) {
      response = await this.post(body, keepalive, null);
    }
    return response;
  }

  private post(
    body: Uint8Array,
    keepalive: boolean,
    token: string | null
  ): Promise<Response> {
    // A bearer replaces the key rather than riding alongside it: the server
    // ignores the key when a bearer is present, so sending both only leaks it.
    const credential: Record<string, string> = token
      ? { Authorization: `Bearer ${token}` }
      : { [RYN_REPLAY_KEY_HEADER]: this.config.ingestKey };
    return this.fetchRef(this.config.ingestUrl, {
      method: 'POST',
      headers: {
        ...(this.config.extraHeaders ?? {}),
        ...credential,
        'Content-Type': 'application/json',
        'Content-Encoding': 'gzip',
      },
      body: body as unknown as BodyInit,
      keepalive,
      // The header above is the only credential; no cookie should ride along.
      credentials: 'omit',
    });
  }
}

/**
 * The one session id every envelope in the batch shares, or null. A token is
 * bound to a single session and the server cross-checks every envelope, so a
 * mixed batch goes out on the key instead of failing the sid check.
 */
function singleSessionId(envelopes: IRynReplayEnvelope[]): string | null {
  const first = envelopes[0]?.sessionId;
  if (!first) {
    return null;
  }
  return envelopes.every((envelope) => envelope.sessionId === first)
    ? first
    : null;
}
